import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Badge, Button, Input, Space, Tag, Dropdown, message as antMessage, Empty, Spin } from 'antd'
import { MessageOutlined, SendOutlined, LikeOutlined, DeleteOutlined, ReloadOutlined, SmileOutlined, PlusOutlined, PictureOutlined, EnvironmentOutlined, FileOutlined } from '@ant-design/icons'
import api from '../../api'

const ALL_EMOJIS = [
  '😀','😃','😄','😁','😆','😅','🤣','😂','🙂','😊','😇','🥰','😍','🤩','😘','😗','😚','😋','😛','😜','🤪','😝','🤑','🤗','🤭','🤫','🤔','🤐','🤨','😐','😑','😶','😏','😒','🙄','😬','😮','🤥','😌','😔','😪','🤤','😴','😷','🤒','🤕','🤢',
  '😵','🤯','🤠','🥳','🥸','😎','🤓','🧐','😕','😟','🙁','😮‍💨','😤','😡','😠','🤬','😈','👿','💀','☠️','💩','🤡','👹','👺','👻','👽','👾','🤖','😺','😸','😹','😻','😼','😽','🙀','😿','😾',
  '👍','👎','👏','🙌','🤝','💪','✊','👊','🤛','🤜','🙏','✌️','🤞','🤟','🤘','👌','🤌','🤏','👈','👉','👆','👇','☝️','✋','🤚','🖐️','🖖','👋','🤙','💅',
  '❤️','🧡','💛','💚','💙','💜','🤎','🖤','🤍','💔','💕','💞','💓','💗','💖','💘','💝','💟','🔥','⭐','🌟','✨','💫','🎉','🎊','🎈','🎁','🏆','🥇','🥈','🥉','💯','✅','❌','⚠️','🚫','💤','💢','💦','💨','🕳️',
  '🌞','🌝','🌚','🌈','☀️','⛅','🌧️','⛈️','🌩️','❄️','☃️','⛄','💧','🌊',
  '🎵','🎶','🎼','🎤','🎧','📻','🎷','🎸','🎹','🎺','🎻','🥁',
  '☕','🍵','🍺','🍻','🥂','🍷','🥃','🍸','🍹','🧉','🍾','🍶',
  '🎂','🍰','🧁','🍬','🍭','🍫','🍿','🍩','🍪','🎄','🎃','🎆','🎇','🧨','🎋','🎍','🎎','🎏','🎐','🎑',
  '📱','💻','⌨️','🖥️','🖨️','🖱️','💿','📷','📹','🎥','📽️','🎞️','📞','☎️','📟','📠','📺','📻','🎙️','⏰','⏳','⌛','📡','🔋','🔌','💡','🔦','🕯️','🗑️',
  '💵','💴','💶','💷','💰','🪙','💳','💎','⚖️','🔧','🔨','⚒️','🛠️','⛏️','🔩','⚙️','🗜️','🧲','🔫','💣','🧨','🪓','🔪','🗡️','⚔️',
  '🏠','🏡','🏢','🏣','🏤','🏥','🏦','🏨','🏩','🏪','🏫','🏬','🏭','🏯','🏰','💒','🗼','🗽','⛪','🕌','🛕','🕍','⛩️','🕋',
  '🚗','🚕','🚙','🚌','🚎','🏎️','🚓','🚑','🚒','🚐','🛻','🚚','🚛','🚜','🏍️','🛵','🚲','🛴','🚄','🚅','🚈','🚝','🚞','🚃','🚋','✈️','🚀','🛸','🚁','🛶','⛵','🚤','🛥️','🛳️','⛴️','🚢'
]

function InteractionDetail({ selectedAccount, selectedAccounts, templates, selectedTemplateContent, onTemplateUsed }) {
  const [activeTab, setActiveTab] = useState('comments')
  const [comments, setComments] = useState([])
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(false)
  const [replyText, setReplyText] = useState('')
  const [replyTarget, setReplyTarget] = useState(null)
  const [showEmoji, setShowEmoji] = useState(false)
  const [sending, setSending] = useState(false)
  const [batchReplying, setBatchReplying] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const inputRef = useRef(null)

  const loadData = useCallback(async () => {
    if (!selectedAccount && selectedAccounts?.length === 0) return
    const accId = selectedAccount?.account_id || selectedAccounts?.[0]
    if (!accId) return
    setLoading(true)
    setErrorMsg('')
    const [commentsRes, messagesRes] = await Promise.all([
      api.listComments({ accountId: accId }),
      api.listMessages({ accountId: accId })
    ])
    setComments(commentsRes.comments || [])
    setMessages(messagesRes.messages || [])
    if (!commentsRes.success && commentsRes.message) setErrorMsg(commentsRes.message)
    if (!messagesRes.success && messagesRes.message) setErrorMsg(prev => prev || messagesRes.message)
    setLoading(false)
  }, [selectedAccount, selectedAccounts])

  useEffect(() => { loadData() }, [loadData])

  useEffect(() => {
    if (selectedTemplateContent) {
      setReplyText(selectedTemplateContent)
      onTemplateUsed && onTemplateUsed()
      inputRef.current?.focus()
    }
  }, [selectedTemplateContent, onTemplateUsed])

  const insertEmoji = (emoji) => {
    setReplyText(prev => prev + emoji)
    setShowEmoji(false)
    inputRef.current?.focus()
  }

  const handleSelectReply = (item) => {
    setReplyTarget(item)
    inputRef.current?.focus()
  }

  const handleSendReply = async () => {
    if (!replyText.trim()) return antMessage.warning('请输入回复内容')
    const acc = selectedAccount || (selectedAccounts?.length === 1 ? { account_id: selectedAccounts[0] } : null)
    if (!acc) return antMessage.warning('请先选择一个账号')
    if (!replyTarget) return antMessage.warning('请先选择一条评论或私信')

    setSending(true)
    let res
    if (activeTab === 'comments') {
      res = await api.replyComment({
        accountId: acc.account_id,
        commentId: replyTarget.commentId || replyTarget.id,
        text: replyText
      })
    } else {
      res = await api.replyMessage({
        accountId: acc.account_id,
        conversationId: replyTarget.conversationId || replyTarget.id,
        text: replyText
      })
    }
    setSending(false)
    if (res.success) {
      antMessage.success('回复成功')
      setReplyText('')
      setReplyTarget(null)
      loadData()
    } else {
      antMessage.error(res.message || '回复失败')
    }
  }

  const handleLike = async (item) => {
    const acc = selectedAccount || (selectedAccounts?.length === 1 ? { account_id: selectedAccounts[0] } : null)
    if (!acc) return
    await api.likeComment({ accountId: acc.account_id, commentId: item.commentId || item.id })
    antMessage.success('已点赞')
  }

  const handleDelete = async (item) => {
    const acc = selectedAccount || (selectedAccounts?.length === 1 ? { account_id: selectedAccounts[0] } : null)
    if (!acc) return
    await api.deleteInteractionComment({ accountId: acc.account_id, commentId: item.commentId || item.id })
    loadData()
  }

  const handleBatchReply = async () => {
    if (!replyText.trim()) return antMessage.warning('请输入回复内容')
    if (selectedAccounts?.length === 0) return antMessage.warning('请选择账号')
    setBatchReplying(true)
    let total = 0
    let done = 0
    for (const accId of selectedAccounts) {
      const cmtRes = await api.listComments({ accountId: accId })
      const msgRes = await api.listMessages({ accountId: accId })
      const commentItems = (cmtRes.comments || []).map(c => ({ commentId: c.commentId || c.id, text: replyText }))
      const messageItems = (msgRes.messages || []).map(m => ({ conversationId: m.conversationId || m.id, text: replyText }))
      total += commentItems.length + messageItems.length
      if (commentItems.length) {
        const r = await api.batchReplyComments({ accountId: accId, items: commentItems })
        done += r.results?.success || 0
      }
      if (messageItems.length) {
        const r = await api.batchReplyMessages({ accountId: accId, items: messageItems })
        done += r.results?.success || 0
      }
    }
    setBatchReplying(false)
    antMessage.success(`批量回复：${done}/${total} 条成功`)
    setReplyText('')
    setReplyTarget(null)
    loadData()
  }

  const handleAttachFromLibrary = async () => {
    const res = await api.listAttachments()
    const atts = res.attachments || []
    if (atts.length === 0) { antMessage.warning('附件库为空，请先在右侧上传文件'); return }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/interaction-attachment'))
      if (data && data.fileName) {
        setReplyText(prev => prev + ` [附件:${data.fileName}]`)
        antMessage.success(`已添加附件: ${data.fileName}`)
      }
    } catch {}
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setDragOver(true)
  }

  const listData = activeTab === 'comments' ? comments : messages
  const isEmpty = listData.length === 0

  const items = [
    { key: 'comments', label: <span style={{ fontSize: 13 }}>评论 {comments.length}</span> },
    { key: 'messages', label: <span style={{ fontSize: 13 }}>私信 {messages.length}</span> }
  ]

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 顶部工具栏 */}
      <div style={{
        padding: '8px 16px', borderBottom: '1px solid #e8e8e8',
        display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0
      }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          {selectedAccount
            ? (selectedAccount.nickname || selectedAccount.remark || '账号')
            : selectedAccounts?.length > 0
              ? `已选 ${selectedAccounts.length} 个账号`
              : '请选择账号'}
        </span>
        <Button size="small" icon={<ReloadOutlined />} onClick={loadData} loading={loading}>刷新</Button>
        {selectedAccounts?.length > 1 && replyText.trim() && (
          <Button size="small" type="primary" onClick={handleBatchReply} loading={batchReplying}>
            批量回复全部 ({selectedAccounts.length}账号)
          </Button>
        )}
      </div>

      {errorMsg && (
        <div style={{ padding: '6px 16px', background: '#fff2f0', borderBottom: '1px solid #ffccc7', fontSize: 12, color: '#ff4d4f' }}>
          {errorMsg} — 请确认账号已登录且浏览器窗口已打开创作者中心页面
        </div>
      )}

      {/* 主体：左右布局 */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* 左侧列表区 */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: '1px solid #f0f0f0', overflow: 'hidden' }}>
          {/* 标签切换 */}
          <div style={{ display: 'flex', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
            {items.map(item => (
              <div key={item.key}
                onClick={() => { setActiveTab(item.key); setReplyTarget(null) }}
                style={{
                  padding: '10px 20px', cursor: 'pointer', fontSize: 13,
                  borderBottom: activeTab === item.key ? '2px solid #1677ff' : '2px solid transparent',
                  color: activeTab === item.key ? '#1677ff' : '#666',
                  fontWeight: activeTab === item.key ? 600 : 400,
                  transition: 'all 0.15s'
                }}>
                {item.label}
              </div>
            ))}
          </div>

          {/* 列表内容 */}
          <div style={{ flex: 1, overflow: 'auto' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
            ) : !selectedAccount && selectedAccounts?.length === 0 ? (
              <Empty description="请在左侧选择一个在线账号" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 80 }} />
            ) : isEmpty ? (
              <Empty description={activeTab === 'comments' ? '暂无评论' : '暂无私信'} image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 60 }} />
            ) : (
              listData.map(item => {
                const itemId = activeTab === 'comments' ? (item.commentId || item.id) : (item.conversationId || item.id)
                const isTarget = replyTarget && (
                  (activeTab === 'comments' && (replyTarget.commentId || replyTarget.id) === itemId) ||
                  (activeTab === 'messages' && (replyTarget.conversationId || replyTarget.id) === itemId)
                )
                return (
                  <div key={itemId}
                    onClick={() => handleSelectReply(item)}
                    style={{
                      padding: '8px 12px', borderBottom: '1px solid #f5f5f5',
                      cursor: 'pointer',
                      background: isTarget ? '#e6f4ff' : 'transparent',
                      borderLeft: isTarget ? '3px solid #1677ff' : '3px solid transparent',
                      transition: 'background 0.1s'
                    }}
                    onMouseEnter={e => { if (!isTarget) e.currentTarget.style.background = '#fafafa' }}
                    onMouseLeave={e => { if (!isTarget) e.currentTarget.style.background = 'transparent' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>
                          {item.nickname || item.author || '用户'}
                          {item.replied && <Tag color="green" style={{ marginLeft: 4, fontSize: 10 }}>已回</Tag>}
                        </div>
                        <div style={{ fontSize: 13, color: '#333', wordBreak: 'break-all', lineHeight: 1.5 }}>
                          {item.content || item.text || item.lastMessage}
                        </div>
                        <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{item.time || item.createdAt}</div>
                      </div>
                      <Space size="small" style={{ flexShrink: 0 }}>
                        <Button size="small" type="text" icon={<MessageOutlined style={{ fontSize: 12 }} />}
                          onClick={e => { e.stopPropagation(); handleSelectReply(item) }}
                          style={{ padding: '0 4px', height: 22 }}>回复</Button>
                        {activeTab === 'comments' && (
                          <Button size="small" type="text" icon={<LikeOutlined style={{ fontSize: 12 }} />}
                            onClick={e => { e.stopPropagation(); handleLike(item) }}
                            style={{ padding: '0 4px', height: 22 }} />
                        )}
                        {activeTab === 'comments' && (
                          <Button size="small" type="text" danger icon={<DeleteOutlined style={{ fontSize: 12 }} />}
                            onClick={e => { e.stopPropagation(); handleDelete(item) }}
                            style={{ padding: '0 4px', height: 22 }} />
                        )}
                      </Space>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>

        {/* 右侧回复区 */}
        <div style={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', background: '#fafafa' }}>
          {/* 回复目标提示 */}
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #e8e8e8' }}>
            {replyTarget ? (
              <div style={{ fontSize: 12, color: '#1677ff' }}>
                回复: <strong>{replyTarget.nickname || replyTarget.author || '用户'}</strong>
                <div style={{ fontSize: 11, color: '#999', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {(replyTarget.content || replyTarget.text || replyTarget.lastMessage || '').slice(0, 40)}
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: '#999' }}>点击左侧消息开始回复</div>
            )}
          </div>

          {/* 回复输入框 + 拖拽区 */}
          <div style={{ flex: 1, padding: '8px 12px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={() => setDragOver(false)}
              style={{
                flex: 1, borderRadius: 6,
                border: dragOver ? '2px dashed #1677ff' : '2px dashed transparent',
                background: dragOver ? '#e6f4ff' : 'transparent',
                transition: 'all 0.2s', display: 'flex', flexDirection: 'column'
              }}>
              {dragOver && (
                <div style={{ textAlign: 'center', padding: 8, fontSize: 12, color: '#1677ff' }}>
                  释放以添加附件到回复
                </div>
              )}
              <Input.TextArea
                ref={inputRef}
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                placeholder="输入回复内容...  /  拖拽右侧附件到此处"
                style={{ flex: 1, resize: 'none', border: 'none', background: 'transparent' }}
                styles={{ textarea: { height: '100%' } }}
              />
            </div>
          </div>

          {/* 工具栏：表情 + 附件按钮 — 放在输入框下方 */}
          <div style={{ padding: '6px 12px', borderTop: '1px solid #f0f0f0', display: 'flex', gap: 4, position: 'relative', flexShrink: 0 }}>
            <div style={{ position: 'relative' }}>
              <Button size="small" type="text" icon={<SmileOutlined />}
                onClick={() => setShowEmoji(!showEmoji)} />
              {showEmoji && (
                <div style={{
                  position: 'absolute', bottom: '100%', left: -4, zIndex: 1000,
                  background: '#fff', border: '1px solid #d9d9d9', borderRadius: 8,
                  padding: 8, boxShadow: '0 -4px 20px rgba(0,0,0,0.15)', width: 248,
                  maxHeight: 200, overflowY: 'auto'
                }}>
                  <div style={{ fontSize: 11, color: '#999', marginBottom: 6, borderBottom: '1px solid #f0f0f0', paddingBottom: 4, position: 'sticky', top: 0, background: '#fff' }}>
                    表情 ({ALL_EMOJIS.length})
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
                    {ALL_EMOJIS.map(e => (
                      <div key={e} onClick={() => insertEmoji(e)}
                        style={{ cursor: 'pointer', fontSize: 20, padding: 3, textAlign: 'center', borderRadius: 4 }}
                        onMouseEnter={e2 => e2.currentTarget.style.background = '#f0f0f0'}
                        onMouseLeave={e2 => e2.currentTarget.style.background = 'transparent'}>
                        {e}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <Dropdown menu={{
              items: [
                { key: 'image', icon: <PictureOutlined />, label: '发送图片', onClick: handleAttachFromLibrary },
                { key: 'file', icon: <FileOutlined />, label: '发送文件', onClick: handleAttachFromLibrary },
                { key: 'location', icon: <EnvironmentOutlined />, label: '发送位置', onClick: () => antMessage.info('位置功能需在平台创作者中心直接操作') }
              ]
            }}>
              <Button size="small" type="text" icon={<PlusOutlined />} />
            </Dropdown>
          </div>

          {/* 发送按钮 */}
          <div style={{ padding: '8px 12px', borderTop: '1px solid #e8e8e8' }}>
            <Button type="primary" icon={<SendOutlined />} block onClick={handleSendReply}
              loading={sending} disabled={!replyText.trim() || !replyTarget}>
              发送回复
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default InteractionDetail
